import api from './api'
import { API_ENDPOINTS, parseApiUrl } from '../utils/constants'

class PurchaseService {
  /**
   * Get paginated purchase history for the current user
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (0-based)
   * @param {number} params.pageSize - Items per page
   * @param {string} params.status - Payment status filter (optional)
   * @param {string} params.startDate - Start date filter YYYY-MM-DD (optional)
   * @param {string} params.endDate - End date filter YYYY-MM-DD (optional)
   * @param {string} params.ticker - Ticker search filter (optional)
   * @returns {Promise<Object>} Paginated purchase history
   */
  async getPurchaseHistory(params = {}) {
    const {
      page = 0,
      pageSize = 10,
      status,
      startDate,
      endDate,
      ticker
    } = params

    const requestBody = {
      page,
      pageSize,
      // Only include optional parameters if they have values
      ...(status && { status }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      ...(ticker && ticker.trim() && { ticker: ticker.trim() })
    }

    const response = await api.post(API_ENDPOINTS.PURCHASE_HISTORY, requestBody)

    return response.data.data || response.data
  }

  /**
   * Get purchase history by status
   * @param {string} status - Payment status
   * @param {Object} params - Additional query parameters
   * @returns {Promise<Object>} Paginated purchase history filtered by status
   */
  async getPurchaseHistoryByStatus(status, params = {}) {
    return this.getPurchaseHistory({ ...params, status })
  }

  /**
   * Search purchase history by ticker
   * @param {string} ticker - Stock ticker to search
   * @param {Object} params - Additional query parameters
   * @returns {Promise<Object>} Paginated purchase history filtered by ticker
   */
  async searchPurchaseHistoryByTicker(ticker, params = {}) {
    return this.getPurchaseHistory({ ...params, ticker })
  }

  /**
   * Get purchase history within date range
   * @param {string} startDate - Start date YYYY-MM-DD
   * @param {string} endDate - End date YYYY-MM-DD
   * @param {Object} params - Additional query parameters
   * @returns {Promise<Object>} Paginated purchase history filtered by date range
   */
  async getPurchaseHistoryByDateRange(startDate, endDate, params = {}) {
    return this.getPurchaseHistory({ ...params, startDate, endDate })
  }

  /**
   * Get first page of purchase history (latest records)
   * @param {Object} params - Additional query parameters
   * @returns {Promise<Object>} First page of purchase history
   */
  async getLatestPurchaseHistory(params = {}) {
    return this.getPurchaseHistory({ page: 0, ...params })
  }
}

export default new PurchaseService()